// Updated Login.jsx with JWT decode-based Google login integration

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import styles from './Login.module.css';
import { registerUserMeta, registerAppForUser } from '../../api/ApiMaster';

function Login({ setUser }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    document.body.classList.add('login-page');
    const isGoogleLoggedIn = localStorage.getItem('googleLoggedIn');
    const storedRole = localStorage.getItem('userRole');

    if (isGoogleLoggedIn && storedRole) {
      setUser({ name: 'Google User', email: 'user@google.com', avatar: '', role: storedRole });
      alert('Welcome back!');
      navigate('/dashboard');
    }

    return () => {
      document.body.classList.remove('login-page');
    };
  }, [navigate, setUser]);

  const handleGoogleSuccess = async (response) => {
    if (!role) {
      alert('Please select a role before logging in.');
      return;
    }

    try {
      const data = jwtDecode(response.credential);

      const userProfile = {
        name: data.name,
        email: data.email,
        avatar: data.picture,
        license: { plan: 'StoryBook Premium', status: 'Active' },
        access: { apps: ['storybook'] },
        personality: { type: 'INFP', tone: 'empathetic' },
      };

      const registered = await registerUserMeta(userProfile);
      if (!registered?.uuid) {
        console.warn('Registration failed or user already exists, attempting fallback fetch');
        const fallback = await fetch(`https://kindred-cloudflare.shepherdn.workers.dev/api/user/get/${encodeURIComponent(data.email)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ passcode: '' })
        });
        const existing = await fallback.json();
        if (!fallback.ok || !existing.uuid) {
          alert('Registration failed. Please try again.');
          return;
        }
        registered.uuid = existing.uuid;
      }

      console.log('User UUID:', registered.uuid);
      localStorage.setItem('userUUID', registered.uuid);

      await registerAppForUser(userProfile.email, 'storybook');

      localStorage.setItem('googleLoggedIn', true);
      localStorage.setItem('userRole', role);
      setUser({ ...userProfile, uuid: registered.uuid, role });
      alert('Google Login successful!');
      navigate('/dashboard');
    } catch (err) {
      console.error('Google login handling failed:', err);
      alert('Error during Google login. Try again.');
    }
  };

  const handleGoogleError = () => {
    console.error('Google Login Failed');
    alert('Google Login failed. Please try again.');
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!role) {
      alert('Please select a role before logging in.');
      return;
    }

    const isGoogleLoggedIn = localStorage.getItem('googleLoggedIn');
    if (isGoogleLoggedIn) {
      alert('Login successful via Google!');
      navigate('/dashboard');
      return;
    }

    if (email === 'user@example.com' && password === 'password') {
      alert('Login successful!');
      localStorage.setItem('userRole', role);
      setUser({ name: 'User', email, avatar: '', role });
      navigate('/dashboard');
    } else {
      alert('Invalid credentials. Please try again.');
    }
  };

  return (
    <GoogleOAuthProvider clientId="767450657361-c1arvbqpisqt92qpcf4596gkc6rsqbij.apps.googleusercontent.com">
      <div className={styles.loginWrapper}>
        <div className={styles.loginFormArea}>
          <div className={styles.loginContainer}>
            <h1>Login</h1>
            <div className={styles.formGroup}>
              <label>Select Role:</label>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px', gap: '10px' }}>
                <button
                  type="button"
                  onClick={() => setRole('employee')}
                  className={role === 'employee' ? styles.selectedEmployee : ''}
                >
                  Employee
                </button>
                <button
                  type="button"
                  onClick={() => setRole('reseller')}
                  className={role === 'reseller' ? styles.selectedReseller : ''}
                >
                  Reseller
                </button>
                <button
                  type="button"
                  onClick={() => setRole('admin')}
                  className={role === 'admin' ? styles.selectedAdmin : ''}
                >
                  Admin
                </button>
              </div>
            </div>
            <form onSubmit={handleSubmit}>
              <div className={styles.formGroup}>
                <label htmlFor="email">Email:</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="password">Password:</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <button type="submit">Login</button>
            </form>
            <div style={{ marginTop: '20px' }}>
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
              />
            </div>
          </div>
        </div>
        <div className={styles.loginImageArea}></div>
      </div>
    </GoogleOAuthProvider>
  );
}

export default Login;
